/* eslint-disable react-hooks/exhaustive-deps */
import {
  AttemptRequestBody,
  CurrentActivityResponse,
  LinkService,
  PathwayService,
  StartActivityRequestBody
} from "@imaginelearning/mps-api-service";
import { UxBackground } from "@imaginelearning/mps-ux-component-library";
import { PathwayContext } from "context-providers/pathway.provider";
import { RewardContext } from "context-providers/reward.provider";
import { useContext, useState } from "react";
import useGrade from "components/liftoff/hooks/useGrade";
import useUser from "../../hooks/useuser";
import "./edge-pathway-activity.scss";
import useProductOriginDomain from "../../hooks/linkplayer/useProductOriginDomain";
import LinkPlayerWithModals, { IActivity } from "../link-launch/linkplayerwithmodals";
import useTheme from "../../hooks/usetheme";

interface IEdgePathwayActivityProps {
  lessonId?: string;
  lessonVersion?: number;
  lessonTitle?: string;
  lessonAttemptId?: string;
  isSpanish?: boolean;
  calculatorType?: string;
}

function EdgePathwayActivity(props: IEdgePathwayActivityProps) {
  const { avatar, background } = useUser();
  const { pathway, currentActivity, fetchCurrentActivity } = useContext(PathwayContext);
  const { addNewRewards } = useContext(RewardContext);
  const { is6to12 } = useGrade();
  const [isLessonCompleted, setIsLessonCompleted] = useState<boolean>(false);
  const { domain } = useProductOriginDomain();
  const { theme } = useTheme();

  const getNextActivity = async () => {
    const nextActivity = await fetchCurrentActivity(pathway.response.studentId, pathway.response.studentPathwayId);

    return {
      pathID: `${nextActivity.response?.activityId}`,
      takeID: nextActivity.response?.attemptId,
      version: nextActivity.response?.version,
      assignmentStarted: true,
      userID: pathway.response.studentId,
      ilpID: pathway.response.studentPathwayId,
      isLessonCompleted,
      activityType: nextActivity.response?.activityType,
      title: nextActivity.response?.title,
      isLast: nextActivity.response?.isLast
    };
  };

  const createAttempt = async () => {
    const { studentId } = pathway.response;
    const result = await LinkService.createAttempt(studentId, attemptRequestBody(currentActivity.response));
    return result.response;
  };

  const updateStatus = async (attemptId: string, status: string) => {
    const response: IActivity = {
      totalQuestions: 0,
      correctAnswers: 0,
      scorePercent: 0,
      pointsEarned: 0,
      lesson: { isCompleted: false },
      isSpanish: false,
      takeID: ""
    };

    switch (status) {
      case "Completed":
        try {
          const result = await PathwayService.currentActivityComplete(
            pathway.response.studentId,
            pathway.response.studentPathwayId
          );

          if (result?.response?.isLast) {
            try {
              const result = await PathwayService.currentLessonScore(
                pathway.response.studentId,
                props.lessonAttemptId || ""
              );

              if (result?.response) {
                const { totalPossibleCorrect, totalCorrect, scorePercent, pointsEarned } = result.response;
                response.totalQuestions = totalPossibleCorrect;
                response.correctAnswers = totalCorrect;
                response.scorePercent = Math.round(scorePercent * 100);
                response.pointsEarned = pointsEarned ?? 0;
              } else {
                console.error("No data available for lesson score.");
              }

              if (response.pointsEarned > 0) {
                await addNewRewards(response.pointsEarned);
              }
            } catch (error) {
              console.error("Error getting lesson score:", error);
            }
            setIsLessonCompleted(true);
            response.lesson = { isCompleted: true };
          }
        } catch (error) {
          console.error("Error marking activity complete:", error);
        }
        break;
      case "InProgress":
        await PathwayService.currentActivityStart(
          startActivityBody(),
          pathway.response.studentId,
          pathway.response.studentPathwayId
        );
        break;
      default:
        console.error("Invalid status:", status);
        break;
    }

    return response;
  };

  const startActivityBody = (): StartActivityRequestBody => ({
    language: props.isSpanish ? "Spanish" : "English"
  });

  const attemptRequestBody = (response: CurrentActivityResponse): AttemptRequestBody => ({
    pathId: response.activityId,
    attemptId: response.attemptId,
    pathVersion: response.version.toString(),
    productName: "Tier 1 Pathway",
    introMode: "None",
    outroMode: "None",
    language: props.isSpanish ? "es" : "en",
    ctkEnvUrl: window.STUDENT_ENV?.REACT_APP_PATHWAY_PLAYER_CTK_ENV_URL ?? "",
    ctkEnvId: window.STUDENT_ENV?.REACT_APP_PATHWAY_PLAYER_CTK_ENV_ID ?? 0,
    metadata: {
      parent_hierarchy_id: `urn:uuid:${props.lessonId}`,
      parent_hierarchy_version: props.lessonVersion,
      parent_hierarchy_title: props.lessonTitle,
      lesson_attempt_id: props.lessonAttemptId,
      tags: `Mastery Pathway|${pathway.response.studentPathwayId}`
    },
    productOriginDomain: domain
  });

  return (
    <div className="mps-link-player">
      <UxBackground
        background={background}
        className={`position-fixed z-index--1 ${is6to12 ? "background-right-bottom" : ""}`}
        theme={theme}
      />
      <LinkPlayerWithModals
        lessonId={props.lessonId}
        lessonVersion={props.lessonVersion}
        lessonTitle={props.lessonTitle}
        avatar={avatar}
        ctkEnvId={window.STUDENT_ENV?.REACT_APP_PATHWAY_PLAYER_CTK_ENV_ID ?? 0}
        ctkEnvUrl={window.STUDENT_ENV?.REACT_APP_PATHWAY_PLAYER_CTK_ENV_URL ?? ""}
        useScoreFromService={false}
        isSpanish={props.isSpanish}
        calculatorType={props.calculatorType}
        updateStatus={updateStatus}
        getNextActivity={getNextActivity}
        createAttempt={createAttempt}
        shouldUseSpeechSettings={true}
      />
    </div>
  );
}

export default EdgePathwayActivity;
